import { createContext, useContext, useMemo, useState } from "react";
import axios from "axios";

import { API_URL } from "./_lib/config";
import Service from "../api/service";
import { useAdminContext } from "./admin/AdminContext";

const CountryContext = createContext(null);

export const useCountryContext = () => {
  const v = useContext(CountryContext);
  if (!v) {
    throw new Error("useCountryContext must be used inside CountryProvider");
  }
  return v;
};

/**
 * CountryContext manages the country list plus 9 "sub-resources" that live
 * under a country (codes, geo-position, language, currency, ethnic group,
 * religion, transportation, stock market, trade organization, climate).
 *
 * Each sub-resource exposes a POST `create` and a PUT `update/:id`. Instead
 * of writing 18 near-identical methods by hand, we declare them once as a
 * table and generate the methods in a loop.
 */
const SUB_RESOURCES = [
  { create: "addCodes",             update: "updateCodes",             path: "code" },
  { create: "addGeoPosition",       update: "updateGeoPosition",       path: "geo/position" },
  { create: "addLanguage",          update: "updateLanguage",          path: "language" },
  { create: "addCurrency",          update: "updateCurrency",          path: "currency" },
  { create: "addEthnicGroup",       update: "updateEthnicGroup",       path: "ethnic/group" },
  { create: "addReligion",          update: "updateReligion",          path: "religion" },
  { create: "addTransportation",    update: "updateTransportation",    path: "transportation" },
  { create: "addStockMarket",       update: "updateStockMarket",       path: "stock/market" },
  { create: "addTradeOrganization", update: "updateTradeOrganization", path: "trade/organization" },
  { create: "addClimate",           update: "updateClimate",           path: "climate" },
];

const safeCall = async (promise) => {
  let errorResponse = null;
  const response = await promise.catch((error) => {
    errorResponse = error?.response ?? null;
  });
  return response ?? errorResponse;
};

export const CountryProvider = ({ children }) => {
  const [countries, setCountries] = useState(null);
  const [country, setCountry] = useState(null);
  const [count, setCount] = useState(0);
  const [countryStats, setCountryStats] = useState(null);

  const { sysAdmin } = useAdminContext();

  const authConfig = () => ({
    headers: { Authorization: `Bearer ${sysAdmin?.token}` },
  });

  const updateCountryState = async (next) => setCountries(next);

  // ── Country list / single ───────────────────────────────────────────────
  const fetchCountries = async (page = 1, pageSize = 100) => {
    const response = await Service.get(
      `/api/v1/admin/country/all/?page=${page}&pageSize=${pageSize}`,
    );
    const payload = response?.data;
    if (payload) {
      setCountries(payload.data);
      setCount(payload.count);
    }
    return response;
  };

  const fetchCountry = async (country_id) => {
    const response = await safeCall(
      axios.get(`${API_URL}/api/v1/country/${country_id}`, authConfig()),
    );
    if (response?.data?.data !== undefined) setCountry(response.data.data);
    return response;
  };

  const fetchCountryStats = async (country_id) => {
    const response = await safeCall(
      axios.get(
        `${API_URL}/api/v1/stats/country/${country_id}`,
        authConfig(),
      ),
    );
    if (response?.data?.data !== undefined) setCountryStats(response.data.data);
    return response;
  };

  const addCountry = async (payload) =>
    safeCall(
      axios.post(`${API_URL}/api/v1/admin/country/create`, payload, authConfig()),
    );

  const updateCountry = async (country_id, payload) =>
    Service.put(`/api/v1/admin/country/update/${country_id}`, payload);

  const deleteCountry = async (country_id) =>
    Service.delete(`/api/v1/admin/country/delete/${country_id}`);

  // ── Sub-resource CRUD, generated from the table above ──────────────────
  const subResourceMethods = useMemo(() => {
    const methods = {};
    for (const { create, update, path } of SUB_RESOURCES) {
      methods[create] = async (payload) =>
        safeCall(
          axios.post(
            `${API_URL}/api/v1/admin/${path}/create`,
            payload,
            authConfig(),
          ),
        );
      methods[update] = async (id, payload) =>
        safeCall(
          axios.put(
            `${API_URL}/api/v1/admin/${path}/update/${id}`,
            payload,
            authConfig(),
          ),
        );
    }
    return methods;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sysAdmin?.token]);

  const value = useMemo(
    () => ({
      countries,
      count,
      country,
      countryStats,
      setCountry,
      setCountryStats,
      fetchCountries,
      fetchCountry,
      fetchCountryStats,
      addCountry,
      updateCountry,
      deleteCountry,
      updateCountryState,
      ...subResourceMethods,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [countries, count, country, countryStats, subResourceMethods],
  );

  return (
    <CountryContext.Provider value={value}>{children}</CountryContext.Provider>
  );
};
